import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Form, Input, Tag } from "antd";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import message from "@/utils/globalMessage";
import PageHeader from "@components/Table/PageHeader";
import ResizableProTable from "@components/Table/ResizableProTable";
import ResponsiveSelect from "@components/ResponsiveSelect";
import {
  SERVICE_REQUEST_PRIORITIES,
  SERVICE_REQUEST_STATUS_OPTIONS,
  SERVICE_REQUEST_STATUS_COLORS,
  normalizeServiceRequestResponse,
} from "./constants";
import "./styles.css";

const EMPTY_FILTERS = {
  title: "",
  category: [],
  priority: [],
  status: [],
};

const getCategoryRows = (response) => {
  const payload = response?.data;
  return Array.isArray(payload) ? payload : payload?.results || [];
};

export default function ServiceRequestList({
  facilityIds = [],
  facilities = [],
  onFacilityChange,
  loadRequests,
  loadCategories,
  getDetailPath,
  createPath,
  showFacilityColumn = false,
  showRequesterColumn = false,
}) {
  const navigate = useNavigate();
  const actionRef = useRef();
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    if (!loadCategories) return undefined;

    let active = true;
    loadCategories()
      .then((response) => {
        if (active) setCategories(getCategoryRows(response));
      })
      .catch(() => {
        if (active) message.error("Failed to load service request categories");
      });

    return () => {
      active = false;
    };
  }, [loadCategories]);

  const request = useCallback(async (params) => {
    if (!facilityIds.length) {
      return { data: [], total: 0, success: true };
    }
    try {
      const response = await loadRequests(facilityIds, {
        page: params.current,
        page_size: params.pageSize,
        title: filters.title.trim(),
        category: filters.category,
        priority: filters.priority,
        status: filters.status,
      });
      const { rows, total } = normalizeServiceRequestResponse(response);
      return { data: rows, total, success: true };
    } catch (error) {
      message.error("Failed to load service requests");
      return { data: [], total: 0, success: false };
    }
  }, [facilityIds, filters, loadRequests]);

  useEffect(() => {
    actionRef.current?.reload?.();
  }, [facilityIds, filters]);

  const openDetail = useCallback((record) => {
    navigate(getDetailPath(record));
  }, [getDetailPath, navigate]);

  const columns = useMemo(() => [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      width: 220,
      render: (value, record) => (
        <button
          type="button"
          className="service-request-table-link"
          onClick={() => openDetail(record)}
        >
          {value}
        </button>
      ),
    },
    ...(showFacilityColumn ? [{
      title: "Facility",
      dataIndex: "facility_name",
      key: "facility_name",
      width: 180,
    }] : []),
    { title: "Suite / Area", dataIndex: "suite_area", key: "suite_area", width: 150 },
    { title: "Category", dataIndex: "category_name", key: "category_name", width: 150 },
    { title: "Priority", dataIndex: "priority", key: "priority", width: 110 },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
    },
    ...(showRequesterColumn ? [{
      title: "Requested By",
      dataIndex: "createdby_name",
      key: "createdby_name",
      width: 160,
    }] : []),
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 115,
      render: (value) => (
        <Tag color={SERVICE_REQUEST_STATUS_COLORS[value]}>{value}</Tag>
      ),
    },
    {
      title: "Created Date",
      dataIndex: "created_date",
      key: "created_date",
      width: 165,
      render: (value) => moment(value).format("YYYY-MM-DD HH:mm"),
    },
  ], [openDetail, showFacilityColumn, showRequesterColumn]);

  const updateFilter = useCallback((key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(EMPTY_FILTERS);
    onFacilityChange?.([]);
  }, [onFacilityChange]);

  const renderSearches = useCallback(() => (
    <Form className="filter-form service-request-filter-form" autoComplete="off">
      {facilities.length > 0 ? (
        <Form.Item label="Facility" name="facility">
          <ResponsiveSelect
            mode="multiple"
            allowClear
            showSearch
            popupTitle="Select Facilities"
            placeholder="Facility"
            value={facilityIds}
            options={facilities.map((item) => ({ value: item.id, label: item.name }))}
            onChange={(value) => onFacilityChange?.(value || [])}
          />
        </Form.Item>
      ) : null}
      <Form.Item label="Title" name="title">
        <Input
          allowClear
          placeholder="Search title"
          value={filters.title}
          onChange={(event) => updateFilter("title", event.target.value)}
        />
      </Form.Item>
      <Form.Item label="Category" name="category">
        <ResponsiveSelect
          mode="multiple"
          allowClear
          showSearch
          popupTitle="Select Categories"
          placeholder="Category"
          value={filters.category}
          options={categories.map((category) => ({
            value: category.id,
            label: category.name,
          }))}
          onChange={(value) => updateFilter("category", value || [])}
        />
      </Form.Item>
      <Form.Item label="Priority" name="priority">
        <ResponsiveSelect
          mode="multiple"
          allowClear
          popupTitle="Select Priorities"
          placeholder="Priority"
          value={filters.priority}
          options={SERVICE_REQUEST_PRIORITIES.map((value) => ({ value, label: value }))}
          onChange={(value) => updateFilter("priority", value || [])}
        />
      </Form.Item>
      <Form.Item label="Status" name="status">
        <ResponsiveSelect
          mode="multiple"
          allowClear
          popupTitle="Select Statuses"
          placeholder="Status"
          value={filters.status}
          options={SERVICE_REQUEST_STATUS_OPTIONS}
          onChange={(value) => updateFilter("status", value || [])}
        />
      </Form.Item>
    </Form>
  ), [categories, facilities, facilityIds, filters, onFacilityChange, updateFilter]);

  const filterHasValue = Boolean(
    facilityIds.length ||
    filters.title.trim() ||
    filters.category.length ||
    filters.priority.length ||
    filters.status.length
  );

  const pageHeader = (
    <PageHeader
      hide_imports
      hide_search
      hid_new={!createPath}
      new_text="Create Service Request"
      onNew={createPath ? () => navigate(createPath) : undefined}
      handleFresh={() => actionRef.current?.reload?.()}
      renderSearches={renderSearches}
      resetFilters={resetFilters}
      filterHasValue={filterHasValue}
    />
  );

  return (
    <div className="service-request-list-page">
      <ResizableProTable
        actionRef={actionRef}
        rowKey="id"
        columns={columns}
        request={request}
        search={false}
        options={false}
        rowSelection={false}
        pagination={{ defaultPageSize: 20, showSizeChanger: true }}
        scroll={{ x: showFacilityColumn || showRequesterColumn ? 1260 : 1100 }}
        toolBarRender={() => pageHeader}
        mobileCardHeaderRender={(record) => (
          <div className="service-request-mobile-card-header">
            <button
              type="button"
              className="service-request-mobile-card-title"
              onClick={() => openDetail(record)}
            >
              {record.title}
            </button>
            <Tag color={SERVICE_REQUEST_STATUS_COLORS[record.status]}>{record.status}</Tag>
          </div>
        )}
        hideMobileColumnActions
        mobileExpandedExcludeKeys={["title", "status"]}
        locale={{ emptyText: facilityIds.length ? "No service requests" : "Select one or more facilities" }}
      />
    </div>
  );
}
